/**
 * Podcast — délka epizody (řez 10).
 *
 * Doménový modul, ne util: převody mezi ISO 8601 trváním z YouTube Data API,
 * sekundami v DB a minutami v adminu jsou pravidla JEDNÉ oblasti, ne obecné
 * nástroje. Proto žije top-level vedle `videoInspiration.ts` / `holidays.ts` /
 * `tripBuckets.ts`.
 *
 * Konzumenti: sync z YouTube (`apps/api/src/lib/podcastSync.ts` parsuje
 * `contentDetails.duration`), karta epizody na webu a editace v adminu.
 */

/**
 * ISO 8601 trvání (tvar, ve kterém YouTube Data API v3 vrací
 * `contentDetails.duration`) → celkový počet sekund.
 *
 * Zvládne `PT45M12S`, `PT1H2M3S`, `PT45M`, `PT30S` i denní tvar `P1DT2H`.
 * Neplatný, prázdný nebo nulový vstup vrací `null` — délka se pak prostě
 * nezobrazí místo toho, aby se do meta řádku dostala nula.
 */
export function parseIso8601Duration(value: string | null | undefined): number | null {
  if (!value) return null;
  const match = /^P(?:(\d+)D)?(?:T(?:(\d+)H)?(?:(\d+)M)?(?:(\d+(?:\.\d+)?)S)?)?$/.exec(
    value.trim(),
  );
  if (!match) return null;

  const [, days, hours, minutes, seconds] = match;
  const total =
    Number(days ?? 0) * 86400 +
    Number(hours ?? 0) * 3600 +
    Number(minutes ?? 0) * 60 +
    Number(seconds ?? 0);

  return total > 0 ? Math.round(total) : null;
}

/**
 * Délka epizody pro meta řádek karty („45 min"). Kratší než minuta i chybějící
 * hodnota vrací `null`, aby se v řádku neobjevilo „0 min" ani „null min".
 */
export function formatEpisodeDuration(seconds: number | null | undefined): string | null {
  if (seconds === null || seconds === undefined || !Number.isFinite(seconds)) return null;
  if (seconds < 60) return null;
  return `${Math.round(seconds / 60)} min`;
}

/**
 * Délka epizody pro editaci v adminu: uložené sekundy → celé minuty (prázdné zůstává prázdné).
 * Protějšek `episodeDurationFromMinutes`.
 */
export function episodeDurationToMinutes(seconds: number | null | undefined): number | null {
  if (seconds === null || seconds === undefined || !Number.isFinite(seconds)) return null;
  return Math.round(seconds / 60);
}

/**
 * Minutové pole zpět na ukládané sekundy. Minuty jsou jen POHLED na sekundy, takže dokud
 * se pohled nezměnil, vrací se původní přesná hodnota ze serveru: sync ukládá např. 2712 s
 * (45,2 min) a slepý převod zpět by z pouhého přeuložení epizody v adminu udělal 2700 s —
 * tedy obsahovou změnu, kterou `shouldLockEpisode` vyhodnotí jako ruční zásah a epizodu
 * natrvalo vyjme ze syncu (AK7).
 */
export function episodeDurationFromMinutes(
  minutes: number | null | undefined,
  storedSeconds: number | null | undefined,
): number | null {
  const edited =
    minutes === null || minutes === undefined || !Number.isFinite(minutes)
      ? null
      : Math.round(minutes);
  if (edited === episodeDurationToMinutes(storedSeconds)) return storedSeconds ?? null;
  return edited === null ? null : edited * 60;
}
